import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useActions } from '../../../providers/actions-provider/actions.context';
import { assetIconCache } from '../../../../store/services/asset-icon-cache/asset-icon-cache';
import { IIconMatchResult, matchIconFiles } from '../../../../utils/asset-type-import';
import { IIconOutcome, IIconProgress, TIconPhase } from './i-import-wizard';

export const RETRY_AFTER_404_MS = 3000;

const isNotFound = (error: unknown): boolean => (
  (error as { response?: { status?: number } })?.response?.status === 404
);

export const iconUploadFailureReason = (error: unknown): string => {
  if (isNotFound(error)) {
    return 'The asset type has not been created yet — upload this icon from its page.';
  }
  return (error as Error)?.message || 'Upload failed.';
};

const delay = (ms: number): Promise<void> => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

export const uploadIconWithRetry = async (
  upload: (code: string, file: File) => Promise<void>,
  code: string,
  file: File,
  retryDelayMs: number = RETRY_AFTER_404_MS,
): Promise<void> => {
  try {
    await upload(code, file);
  } catch (error) {
    if (!isNotFound(error)) {
      throw error;
    }
    await delay(retryDelayMs);
    await upload(code, file);
  }
};

export const supportsDirectoryPicker = (): boolean => (
  typeof HTMLInputElement !== 'undefined' && 'webkitdirectory' in HTMLInputElement.prototype
);

export interface IUseIconUpload {
  phase: TIconPhase;
  match: IIconMatchResult | null;
  progress: IIconProgress;
  outcomes: IIconOutcome[];
  supportsDirectory: boolean;
  onFilesSelected: (files: File[]) => void;
  upload: () => Promise<void>;
}

export const useIconUpload = (createdCodes: string[]): IUseIconUpload => {
  const { assetActions } = useActions();
  const [phase, setPhase] = useState<TIconPhase>('ready');
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<IIconProgress>({ done: 0, total: 0 });
  const [outcomes, setOutcomes] = useState<IIconOutcome[]>([]);
  const mounted = useRef(true);

  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const match = useMemo(
    () => (files.length > 0 ? matchIconFiles(files, createdCodes) : null),
    [files, createdCodes],
  );

  const uploadOne = useCallback(
    (code: string, file: File): Promise<void> => uploadIconWithRetry(
      assetActions.uploadAssetIcon,
      code,
      file,
    ),
    [assetActions],
  );

  const upload = useCallback(async (): Promise<void> => {
    const pending = match?.matched ?? [];
    if (pending.length === 0) {
      return;
    }

    setPhase('uploading');
    setProgress({ done: 0, total: pending.length });
    const results: IIconOutcome[] = [];

    for (let index = 0; index < pending.length; index += 1) {
      const { code, file } = pending[index];
      try {
        // eslint-disable-next-line no-await-in-loop
        await uploadOne(code, file);
        results.push({ code, fileName: file.name, ok: true });
      } catch (error) {
        results.push({
          code, fileName: file.name, ok: false, reason: iconUploadFailureReason(error),
        });
      }
      if (mounted.current) {
        setProgress({ done: index + 1, total: pending.length });
      }
    }

    assetIconCache.clear();

    if (mounted.current) {
      setOutcomes(results);
      setPhase('done');
    }
  }, [match, uploadOne]);

  return {
    phase,
    match,
    progress,
    outcomes,
    supportsDirectory: supportsDirectoryPicker(),
    onFilesSelected: useCallback((selected: File[]) => setFiles(selected), []),
    upload,
  };
};
